import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle, Clock, Eye, User, Building, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

interface KycVerification {
  id: string;
  email: string;
  mobile: string;
  verification_type: 'individual' | 'company';
  status: 'pending' | 'approved' | 'rejected';
  document_count: number;
  submitted_at: string;
  verified_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  verification_data: any;
}

export default function AdminKycDashboard() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState<KycVerification[]>([]);
  const [filteredVerifications, setFilteredVerifications] = useState<KycVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'individual' | 'company'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'email' | 'status'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    fetchVerifications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [verifications, searchTerm, filterType, sortBy, sortOrder, activeTab]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE}/api/admin/kyc/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch verifications');
      }

      const data = await response.json();
      setVerifications(data.data || []);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล');
      setVerifications([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...verifications];

    // Filter by tab (status)
    if (activeTab !== 'all') {
      filtered = filtered.filter(v => v.status === activeTab);
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.email.toLowerCase().includes(searchLower) ||
        v.mobile.includes(searchTerm) ||
        (v.verification_data?.firstName && v.verification_data.firstName.toLowerCase().includes(searchLower)) ||
        (v.verification_data?.lastName && v.verification_data.lastName.toLowerCase().includes(searchLower)) ||
        (v.verification_data?.companyName && v.verification_data.companyName.toLowerCase().includes(searchLower)) ||
        (v.verification_data?.idCard && v.verification_data.idCard.includes(searchTerm)) ||
        (v.verification_data?.taxId && v.verification_data.taxId.includes(searchTerm))
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(v => v.verification_type === filterType);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.submitted_at);
          bValue = new Date(b.submitted_at);
          break;
        case 'email':
          aValue = a.email;
          bValue = b.email;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredVerifications(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">รอดำเนินการ</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">อนุมัติแล้ว</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">ปฏิเสธแล้ว</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handleViewDetail = (verification: KycVerification) => {
    navigate(`/admin/kyc/${verification.id}`);
  };

  const getStatusCounts = () => {
    const pending = verifications.filter(v => v.status === 'pending').length;
    const approved = verifications.filter(v => v.status === 'approved').length;
    const rejected = verifications.filter(v => v.status === 'rejected').length;
    
    return { pending, approved, rejected, total: verifications.length };
  };

  // Pagination
  const totalPages = Math.ceil(filteredVerifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVerifications = filteredVerifications.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">การจัดการ KYC</h1>
          <p className="text-gray-600">จัดการและตรวจสอบข้อมูลการยืนยันตัวตน</p>
        </div>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="ค้นหาด้วย อีเมล, เบอร์โทร, ชื่อ, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={filterType} onValueChange={(value: 'all' | 'individual' | 'company') => setFilterType(value)}>
              <SelectTrigger>
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="ประเภท" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกประเภท</SelectItem>
                <SelectItem value="individual">บุคคลธรรมดา</SelectItem>
                <SelectItem value="company">นิติบุคคล</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(value: 'date' | 'email' | 'status') => setSortBy(value)}>
              <SelectTrigger>
                <SelectValue placeholder="เรียงตาม" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">วันที่</SelectItem>
                <SelectItem value="email">อีเมล</SelectItem>
                <SelectItem value="status">สถานะ</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}>
              <SelectTrigger>
                <SelectValue placeholder="ลำดับ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">ล่าสุดก่อน</SelectItem>
                <SelectItem value="asc">เก่าก่อน</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            ทั้งหมด ({statusCounts.total})
          </TabsTrigger>
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-1" />
            รอดำเนินการ ({statusCounts.pending})
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-1" />
            อนุมัติแล้ว ({statusCounts.approved})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <AlertTriangle className="w-4 h-4 mr-1" />
            ปฏิเสธแล้ว ({statusCounts.rejected})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-6">
              {currentVerifications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>ไม่พบข้อมูลที่ตรงกับเงื่อนไขการค้นหา</p>
                </div>
              ) : (
                <>
                  {/* List Header */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 pb-3 border-b border-gray-200 text-sm font-medium text-gray-500">
                    <div className="col-span-1">ประเภท</div>
                    <div className="col-span-3">ข้อมูลติดต่อ</div>
                    <div className="col-span-3">ข้อมูลยืนยัน</div>
                    <div className="col-span-2">วันที่ส่ง</div>
                    <div className="col-span-1">สถานะ</div>
                    <div className="col-span-2">การดำเนินการ</div>
                  </div>

                  {/* List Items */}
                  <div className="space-y-3 mt-4">
                    {currentVerifications.map((verification) => (
                      <div key={verification.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                          {/* Type Column */}
                          <div className="col-span-1">
                            <div className="flex items-center">
                              {verification.verification_type === 'individual' ? (
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User className="w-4 h-4 text-blue-600" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <Building className="w-4 h-4 text-purple-600" />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Contact Info Column */}
                          <div className="col-span-3">
                            <p className="font-medium text-gray-900 text-sm">{verification.email}</p>
                            <p className="text-xs text-gray-500">{verification.mobile}</p>
                          </div>

                          {/* Verification Info Column */}
                          <div className="col-span-3">
                            {verification.verification_data ? (
                              verification.verification_type === 'individual' ? (
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {verification.verification_data.prefix} {verification.verification_data.firstName} {verification.verification_data.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    ID: {verification.verification_data.idCard}
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {verification.verification_data.companyName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Tax ID: {verification.verification_data.taxId}
                                  </p>
                                </div>
                              )
                            ) : (
                              <p className="text-xs text-gray-400">ไม่มีข้อมูล</p>
                            )}
                          </div>

                          {/* Date Column */}
                          <div className="col-span-2">
                            <p className="text-sm text-gray-900">{formatDate(verification.submitted_at)}</p>
                            <p className="text-xs text-gray-500">{verification.document_count} ไฟล์</p>
                          </div>

                          {/* Status Column */}
                          <div className="col-span-1">
                            {getStatusBadge(verification.status)}
                          </div>

                          {/* Actions Column */}
                          <div className="col-span-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetail(verification)}
                              className="w-full"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              ดูรายละเอียด
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        แสดง {startIndex + 1}-{Math.min(endIndex, filteredVerifications.length)} จาก {filteredVerifications.length} รายการ
                      </p>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        
                        <span className="px-3 py-1 text-sm">
                          หน้า {currentPage} จาก {totalPages}
                        </span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}